import { Controller } from "@hotwired/stimulus"
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  createEditor,
  FORMAT_TEXT_COMMAND,
  HISTORY_MERGE_TAG,
  REDO_COMMAND,
  UNDO_COMMAND,
} from "lexical"
import {
  $createHeadingNode,
  HeadingNode,
  QuoteNode,
  registerRichText,
} from "@lexical/rich-text"
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode,
  registerList,
} from "@lexical/list"
import { LinkNode } from "@lexical/link"
import { createEmptyHistoryState, registerHistory } from "@lexical/history"
import { $setBlocksType } from "@lexical/selection"
import { mergeRegister } from "@lexical/utils"
import { $createImageNode, ImageNode } from "../lexical/image_node"

const MAX_IMAGE_BYTES = 10 * 1024 * 1024
const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
]

const theme = {
  paragraph: "lexical-paragraph",
  heading: {
    h1: "lexical-h1",
    h2: "lexical-h2",
    h3: "lexical-h3",
  },
  quote: "lexical-quote",
  list: {
    ul: "lexical-list-ul",
    ol: "lexical-list-ol",
    listitem: "lexical-list-item",
  },
  link: "lexical-link",
  text: {
    bold: "lexical-text-bold",
    italic: "lexical-text-italic",
    underline: "lexical-text-underline",
  },
}

export default class extends Controller {
  static targets = ["root", "hidden", "fileInput"]

  connect() {
    this.mountedDecorators = new Map()
    this.editor = createEditor({
      namespace: "PostEditor",
      nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, ImageNode],
      theme,
      onError: (error) => { throw error },
    })
    this.editor.setRootElement(this.rootTarget)

    this.unregister = mergeRegister(
      registerRichText(this.editor),
      registerList(this.editor),
      registerHistory(this.editor, createEmptyHistoryState(), 300),
      this.editor.registerDecoratorListener((decorators) => this.mountDecorators(decorators)),
      this.editor.registerUpdateListener(({ editorState }) => {
        this.hiddenTarget.value = JSON.stringify(editorState.toJSON())
      }),
    )

    this.restoreState()
    this.ensureNonEmptyRoot()
  }

  disconnect() {
    if (this.unregister) this.unregister()
    if (this.editor) this.editor.setRootElement(null)
    this.mountedDecorators?.clear()
  }

  mountDecorators(decorators) {
    const seen = new Set()
    for (const [key, decorator] of Object.entries(decorators)) {
      seen.add(key)
      const wrapper = this.editor.getElementByKey(key)
      if (!wrapper || !decorator) continue
      if (this.mountedDecorators.get(key) === decorator) continue
      wrapper.replaceChildren(decorator)
      this.mountedDecorators.set(key, decorator)
    }
    for (const key of [...this.mountedDecorators.keys()]) {
      if (!seen.has(key)) this.mountedDecorators.delete(key)
    }
  }

  restoreState() {
    const initial = this.hiddenTarget.value
    if (!initial) return
    try {
      const parsed = this.editor.parseEditorState(initial)
      this.editor.setEditorState(parsed)
    } catch (err) {
      console.warn("Failed to restore Lexical state:", err)
    }
  }

  ensureNonEmptyRoot() {
    this.editor.update(() => {
      const root = $getRoot()
      if (root.isEmpty()) {
        root.append($createParagraphNode())
      }
    }, { tag: HISTORY_MERGE_TAG })
  }

  bold() { this.editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold") }
  italic() { this.editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic") }
  underline() { this.editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline") }

  heading(event) {
    const level = event.params.level
    const tag = `h${level}`
    this.editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createHeadingNode(tag))
      }
    })
  }

  paragraph() {
    this.editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode())
      }
    })
  }

  bulletList() { this.editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND) }
  orderedList() { this.editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND) }

  undo() { this.editor.dispatchCommand(UNDO_COMMAND) }
  redo() { this.editor.dispatchCommand(REDO_COMMAND) }

  image() {
    if (!this.hasFileInputTarget) return
    this.fileInputTarget.click()
  }

  async fileSelected(event) {
    const input = event.target
    const file = input.files?.[0]
    if (!file) return

    try {
      this.validateFile(file)
      const presigned = await this.requestPresignedUpload(file.type)
      await this.uploadToS3(presigned.upload_url, file)
      const { url } = await this.requestSignedReadUrl(presigned.key)
      this.insertImage({ objectKey: presigned.key, src: url, alt: file.name })
    } catch (err) {
      console.error("Image upload failed:", err)
      alert(err.message || "Image upload failed")
    } finally {
      input.value = ""
    }
  }

  validateFile(file) {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      throw new Error(`Unsupported file type: ${file.type}`)
    }
    if (file.size > MAX_IMAGE_BYTES) {
      throw new Error(`Image is too large (max ${MAX_IMAGE_BYTES / 1024 / 1024} MB)`)
    }
  }

  async requestPresignedUpload(contentType) {
    const response = await fetch("/admin/uploads", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken,
      },
      body: JSON.stringify({ content_type: contentType }),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.error || `Presign request failed (${response.status})`)
    }
    return response.json()
  }

  async requestSignedReadUrl(objectKey) {
    const response = await fetch(`/admin/uploads/sign?key=${encodeURIComponent(objectKey)}`, {
      credentials: "same-origin",
      headers: { "Accept": "application/json" },
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.error || `Sign request failed (${response.status})`)
    }
    return response.json()
  }

  async uploadToS3(uploadUrl, file) {
    const response = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    })
    if (!response.ok) {
      throw new Error(`S3 upload failed (${response.status})`)
    }
  }

  insertImage({ objectKey, src, alt }) {
    this.editor.update(() => {
      const node = $createImageNode({ objectKey, src, alt: alt || "" })
      $insertNodes([node])
    })
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content ?? ""
  }
}
